import * as Headless from '@headlessui/react';
import { useClose } from '@headlessui/react';
import clsx from 'clsx';
import React, { createContext, useContext } from 'react';

import { Button } from '@/common/components/button';
import { DialogDescription, DialogTitle } from '@/common/components/dialog';
import { FaRegularXMarkIcon } from '@/common/components/icons';

type DrawerVariant = 'top' | 'right' | 'bottom' | 'left';

const DrawerContext = createContext<{ variant: DrawerVariant }>({
  variant: 'right'
});

export const Drawer = React.forwardRef<
  React.ComponentRef<typeof Headless.Dialog>,
  React.ComponentPropsWithoutRef<typeof Headless.Dialog> & { variant?: DrawerVariant }
>(({ className, variant = 'right', ...props }, ref) => {
  return (
    <DrawerContext.Provider value={{ variant } as React.ContextType<typeof DrawerContext>}>
      <Headless.Dialog className={clsx(className, 'relative z-10')} {...props} ref={ref} />
    </DrawerContext.Provider>
  );
});

export const DrawerOverlay = React.forwardRef<
  React.ComponentRef<typeof Headless.DialogBackdrop>,
  React.ComponentPropsWithoutRef<typeof Headless.DialogBackdrop>
>(({ className, ...props }, ref) => {
  return <Headless.DialogBackdrop className={clsx(className, 'fixed inset-0 dark:bg-zinc-950/25 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-closed:opacity-0')} {...props} ref={ref} />;
});

export const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof Headless.DialogPanel>,
  Omit<React.ComponentPropsWithoutRef<typeof Headless.DialogPanel>, 'children'> & {
    children: React.ReactNode | ((bag: Headless.DialogPanelProps) => React.ReactElement);
  }
>(({ className, children, ...props }, ref) => {
        const { variant } = useContext(DrawerContext);

        return (
          <DrawerOverlay>
            <div className="fixed inset-0 overflow-hidden">
              <div className="absolute inset-0 overflow-hidden">
                <div className={clsx(
                  'pointer-events-none fixed flex',
                  variant === 'right' && 'right-0 inset-y-0 max-w-full pl-0 md:pl-10'
                )}
                >
                  <Headless.DialogPanel
                    transition
                    className={clsx(
                      className,
                      'pointer-events-auto transform transition duration-500 ease-in-out sm:duration-700',
                      variant === 'right' && 'data-closed:translate-x-full w-screen max-w-2xl'
                    )}
                    ref={ref}
                    {...props}
                  >
                    <div className={clsx(
                      'flex flex-col bg-white dark:bg-zinc-900 py-6 shadow-xl',
                      variant === 'right' && 'h-full overflow-y-scroll'
                    )}
                    >
                      <div className="flex h-full flex-col px-4 sm:px-6 gap-6">
                        {/* TODO: this might cause issues with render prop passing */}
                        {typeof children === 'function' ? children({}) : children}
                      </div>
                    </div>
                  </Headless.DialogPanel>
                </div>
              </div>
            </div>
          </DrawerOverlay>
        );
      });

export function DrawerHeader({ className, children, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  const close = useClose();

  return (
    <div {...props} className={clsx(className, 'flex items-start justify-between')}>
      <div className="flex flex-col">
        {children}
      </div>
      <Button
        plain
        className="h-6 w-6 text-zinc-500 hover:text-zinc-900 dark:text-white dark:hover:text-white"
        onClick={close}
      >
        <FaRegularXMarkIcon />
      </Button>
    </div>
  );
}

export const DrawerTitle = DialogTitle;

export function DrawerFooter({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div {...props} className={clsx(className, 'flex shrink-0 justify-end')} />;
}

export function DrawerBody({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div {...props} className={clsx(className, 'flex-1')} />;
}

export const DrawerDescription = DialogDescription;
